function ready(callback: () => void) {
  if (document.readyState !== "loading") {
    callback();
  } else {
    document.addEventListener("DOMContentLoaded", callback);
  }
}

type FilterState = Record<string, string>;

function getFilterSelects(root: HTMLElement): HTMLSelectElement[] {
  return Array.from(root.querySelectorAll<HTMLSelectElement>("[data-filter]"));
}

function readFilterState(root: HTMLElement): FilterState {
  const state: FilterState = {};

  getFilterSelects(root).forEach((select) => {
    const name = select.dataset.filter;
    if (name) state[name] = select.value;
  });

  return state;
}

function isValidOptionValue(select: HTMLSelectElement, value: string): boolean {
  return Array.from(select.options).some((option) => option.value === value);
}

function writeFilterState(root: HTMLElement, state: FilterState) {
  getFilterSelects(root).forEach((select) => {
    const name = select.dataset.filter;
    if (!name) return;

    const value = state[name];
    if (value && isValidOptionValue(select, value)) {
      select.value = value;
    }
  });
}

function readUrl(): FilterState {
  const state: FilterState = {};
  const params = new URLSearchParams(window.location.search);

  params.forEach((value, key) => {
    if (value) state[key] = value;
  });

  return state;
}

function syncUrl(state: FilterState) {
  const params = new URLSearchParams();

  Object.keys(state).forEach((key) => {
    const value = state[key];
    if (value) params.set(key, value);
  });

  const query = params.toString();
  const url = query
    ? `${window.location.pathname}?${query}`
    : window.location.pathname;

  window.history.replaceState(null, "", url);
}

function hasActiveFilters(root: HTMLElement): boolean {
  return getFilterSelects(root).some((select) => select.value !== "");
}

function matchesFilter(
  card: HTMLElement,
  filterName: string,
  value: string
): boolean {
  if (!value) return true;

  if (filterName === "tag") {
    const tags = (card.dataset.tags || "").split(",").filter(Boolean);
    return tags.includes(value.toLowerCase());
  }

  const cardValue = card.dataset[filterName] || "";
  return cardValue === value;
}

function clearFilters(root: HTMLElement) {
  getFilterSelects(root).forEach((select) => {
    select.value = "";
  });
}

function updateFilters(root: HTMLElement) {
  const cards = Array.from(root.querySelectorAll<HTMLElement>(".program-card"));
  const filters = getFilterSelects(root);
  const emptyMessage = root.querySelector<HTMLElement>(
    ".archive-empty-filtered"
  );
  const statusBar = root.querySelector<HTMLElement>("[data-filter-status]");
  const countEl = root.querySelector<HTMLElement>("[data-filter-count]");
  const countTemplate = root.dataset.filterCountTemplate || "__COUNT__ treff";

  let visibleCount = 0;

  cards.forEach((card) => {
    const visible = filters.every((select) => {
      const filterName = select.dataset.filter;
      if (!filterName) return true;
      return matchesFilter(card, filterName, select.value);
    });

    card.hidden = !visible;
    if (visible) visibleCount += 1;
  });

  if (emptyMessage) {
    emptyMessage.hidden = visibleCount > 0 || cards.length === 0;
  }

  if (statusBar) {
    const active = hasActiveFilters(root);
    statusBar.hidden = !active;

    if (active && countEl) {
      countEl.textContent = countTemplate.replace(
        "__COUNT__",
        String(visibleCount)
      );
    }
  }

  syncUrl(readFilterState(root));
}

function initProgramFilters() {
  const root = document.querySelector<HTMLElement>(
    ".archive[data-program-filters]"
  );
  if (!root) return;

  writeFilterState(root, readUrl());
  updateFilters(root);

  getFilterSelects(root).forEach((select) => {
    select.addEventListener("change", () => updateFilters(root));
  });

  root
    .querySelector<HTMLButtonElement>("[data-filter-clear-all]")
    ?.addEventListener("click", () => {
      clearFilters(root);
      updateFilters(root);
    });
}

export default function programFilters() {
  ready(initProgramFilters);
}
